import type { useForm } from "react-hook-form"
import type * as z from "zod"
import type { FieldConfigItem } from "../types"
import { Plus, Trash } from "lucide-react"
import { useFieldArray } from "react-hook-form"
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"
import { beautifyObjectName, isZodArray, isZodDefault, isZodObject } from "../utils"
import AutoFormObject from "./object"

const ADD_LABEL = "Add"

export default function AutoFormArray<T extends z.ZodTypeAny = z.ZodTypeAny>({
  name,
  item,
  form,
  path = [],
  fieldConfig,
}: {
  name: string
  item: z.ZodArray<T> | z.ZodDefault<z.ZodArray<T>>
  form: ReturnType<typeof useForm>
  path?: string[]
  fieldConfig?: FieldConfigItem<unknown>
}) {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name,
  })
  const title = (item as { _def?: { description?: string } })?._def?.description ?? beautifyObjectName(name)

  // Get the item definition type
  const itemDefType = isZodArray(item)
    ? item._def.type
    : isZodDefault(item)
      ? (item._def.innerType)._def.type
      : null

  return (
    <AccordionItem className={cn("border-none", fieldConfig?.containerClassName)} value={name}>
      <AccordionTrigger>{title}</AccordionTrigger>
      <AccordionContent>
        {fields.map((_field, index) => {
          const key = _field.id
          return (
            <div key={`${key}`} className="mt-4 flex flex-col">
              {isZodObject(itemDefType) && (
                <AutoFormObject
                  fieldConfig={fieldConfig?.fields}
                  form={form}
                  path={[...path, index.toString()]}
                  schema={itemDefType}
                />
              )}
              <div className="my-4 flex justify-end">
                <Button
                  className="hover:bg-zinc-300 hover:text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-white dark:text-black dark:hover:bg-zinc-300 dark:hover:text-black dark:hover:ring-0 dark:hover:ring-offset-0 dark:focus-visible:ring-0 dark:focus-visible:ring-offset-0"
                  onClick={() => remove(index)}
                  size="icon"
                  type="button"
                  variant="secondary"
                >
                  <Trash className="size-4 " />
                </Button>
              </div>
              <Separator />
            </div>
          )
        })}
        <Button
          className="mt-4 flex items-center"
          onClick={() => append({})}
          type="button"
          variant="secondary"
        >
          <Plus className="mr-2" size={16} />
          {ADD_LABEL}
        </Button>
      </AccordionContent>
    </AccordionItem>
  )
}
